import { ethers } from "ethers";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NETWORK } from "src/config/constants";
import { CHAINS, CHAIN_ALIASES } from "src/wallet-connection/chains";
import { useWeb3Connect } from "src/wallet-connection/context/we3-connect-context";

const ProviderContext = React.createContext({
  bscProvider: undefined,
  ftmProvider: undefined,
  ethProvider: undefined,
  currentProvider: undefined,
  wallet: {
    provider: undefined,
    signer: undefined,
  },
});

const ProviderProvider = ({ children }) => {
  const [bscProvider, setBscProvider] = useState(null);
  const [ftmProvider, setFtmProvider] = useState(null);
  const [ethProvider, setEthProvider] = useState(null);
  const [bscTestnetPRovider, setBscTestnetProvider] = useState(null);
  const [provider, setProvider] = useState(null);

  const { chain, connector } = useWeb3Connect();

  const _selectBestRPC = async (rpcUrls) => {
    const promises = rpcUrls.map(async (rpc) => {
      const provider = new ethers.providers.JsonRpcProvider(rpc);
      await provider.getBlockNumber();
      return rpc;
    });
    return await Promise.any(promises);
  };

  const _initBscProvider = useCallback(async () => {
    try {
      const bestRpc = await _selectBestRPC(CHAINS[CHAIN_ALIASES.BSC_MAINNET].urls);
      const _provider = new ethers.providers.JsonRpcProvider(bestRpc);
      setBscProvider(_provider);
    } catch (error) {
      console.error("bsc error: ", error);
    }
  }, []);

  const _initFtmProvider = useCallback(async () => {
    try {
      const bestRpc = await _selectBestRPC(CHAINS[CHAIN_ALIASES.FTM_MAINNET].urls);
      const _provider = new ethers.providers.JsonRpcProvider(bestRpc);
      setFtmProvider(_provider);
    } catch (error) {
      console.error("ftm error: ", error);
    }
  }, []);

  const _initEthProvider = useCallback(async () => {
    try {
      const bestRpc = await _selectBestRPC(CHAINS[CHAIN_ALIASES.ETH_MAINNET].urls);
      const _provider = new ethers.providers.JsonRpcProvider(bestRpc);
      setEthProvider(_provider);
    } catch (error) {
      console.error("eth error: ", error);
    }
  }, []);

  const _initBscTestnetProvider = useCallback(async () => {
    try {
      const bestRpc = await _selectBestRPC(CHAINS[CHAIN_ALIASES.BSC_TESTNET].urls);
      const _provider = new ethers.providers.JsonRpcProvider(bestRpc);
      setBscTestnetProvider(_provider);
    } catch (error) {
      console.error("eth error: ", error);
    }
  }, []);

  const { walletProvider, signer } = useMemo(() => {
    if (connector?.provider) {
      const _provider = new ethers.providers.Web3Provider(connector.provider);
      const signer = _provider.getSigner();
      return { walletProvider: _provider, signer: signer };
    }
    return { walletProvider: null, signer: null };
  }, [chain, connector?.provider]);

  useEffect(() => {
    _initBscProvider();
    _initFtmProvider();
    _initEthProvider();
  }, []);

  useEffect(() => {
    if (chain.chainId === CHAIN_ALIASES.BSC_TESTNET) _initBscTestnetProvider();
  }, [chain.chainId]);

  useEffect(() => {
    if (chain.chainId === CHAIN_ALIASES.BSC_MAINNET) setProvider(bscProvider);
    else if (chain.chainId === CHAIN_ALIASES.FTM_MAINNET) setProvider(ftmProvider);
    else if (chain.chainId === CHAIN_ALIASES.ETH_MAINNET) setProvider(ethProvider);
    else if (chain.chainId === CHAIN_ALIASES.BSC_TESTNET) setProvider(bscTestnetPRovider);
  }, [bscProvider, ftmProvider, ethProvider, bscTestnetPRovider, chain.chainId]);

  const contextData = useMemo(() => {
    return {
      bscProvider,
      ftmProvider,
      ethProvider,
      currentProvider: provider,
      wallet: {
        provider: walletProvider,
        signer: signer,
      },
    };
  }, [bscProvider, ftmProvider, ethProvider, provider, walletProvider, signer]);

  return <ProviderContext.Provider value={contextData}>{children}</ProviderContext.Provider>;
};

ProviderProvider.propTypes = {
  children: PropTypes.node,
};

export default ProviderProvider;

export const useProviderContext = () => React.useContext(ProviderContext);

export const useProviderSelector = (selectorFn) => {
  const data = useProviderContext();
  return useMemo(() => {
    return selectorFn(data);
  }, [data, selectorFn]);
};

export const useNetworkProviderSelector = (network) => {
  const context = React.useContext(ProviderContext);
  return useMemo(() => {
    if (network === NETWORK.BSC_MAINNET) return context?.bscProvider;
    else if (network === NETWORK.FTM_MAINNET) return context?.ftmProvider;
    else if (network === NETWORK.ETH_MAINNET) return context?.ethProvider;
    else return null;
  }, [context, network]);
};
